import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { crmAPI } from '../lib/api';
import { toast } from 'sonner';
import { formatRelativeTime } from '../lib/utils';
import {
  Users, Plus, Search, MoreVertical, Trash2, Edit, Phone, MessageSquare,
  Mail, Upload, CheckCircle, Clock, Send, X, Loader2, RefreshCw,
  Settings, Copy, Key, Wifi, WifiOff,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { Textarea } from '../components/ui/textarea';

const SOURCES = [
  { value: 'all', label: 'All Sources' },
  { value: 'referral', label: 'Referral' },
  { value: 'website', label: 'Website' },
  { value: 'instagram', label: 'Instagram' },
  { value: 'whatsapp', label: 'WhatsApp' },
  { value: 'etsy', label: 'Etsy' },
  { value: 'other', label: 'Other' },
];

const STATUSES = [
  { value: 'all', label: 'All Statuses' },
  { value: 'lead', label: 'Lead' },
  { value: 'customer', label: 'Customer' },
  { value: 'reseller', label: 'Reseller' },
];

const SOURCE_COLORS = {
  referral: 'bg-purple-100 text-purple-700',
  website: 'bg-blue-100 text-blue-700',
  instagram: 'bg-pink-100 text-pink-700',
  whatsapp: 'bg-green-100 text-green-700',
  etsy: 'bg-orange-100 text-orange-700',
  other: 'bg-zinc-100 text-zinc-700',
};

const STATUS_COLORS = {
  lead: 'bg-amber-100 text-amber-700',
  customer: 'bg-emerald-100 text-emerald-700',
  reseller: 'bg-blue-100 text-blue-700',
};

const EMPTY_FORM = { name: '', phone: '', email: '', source: 'other', status: 'lead', notes: '' };

const Contacts = () => {
  const { activeBrand, isFounder } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');
  const [selectedContacts, setSelectedContacts] = useState([]);
  const [selectAll, setSelectAll] = useState(false);

  // Dialogs
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showMessageDialog, setShowMessageDialog] = useState(false);
  const [showAPIKeyDialog, setShowAPIKeyDialog] = useState(false);
  const [showMessagingStatus, setShowMessagingStatus] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  // Form
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [editingContact, setEditingContact] = useState(null);

  // Message
  const [messageChannel, setMessageChannel] = useState('linq');
  const [messageText, setMessageText] = useState('');
  const [messageSubject, setMessageSubject] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const [whatsappMode, setWhatsappMode] = useState('text'); // 'text' or 'template'
  const [templateName, setTemplateName] = useState('');
  const [templateLanguage, setTemplateLanguage] = useState('en');
  const [availableTemplates, setAvailableTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);

  // Import
  const [importing, setImporting] = useState(false);

  // API Key
  const [apiKey, setApiKey] = useState('');
  const [regeneratingKey, setRegeneratingKey] = useState(false);

  // Messaging config
  const [messagingConfig, setMessagingConfig] = useState(null);

  useEffect(() => { fetchContacts(); }, [activeBrand, statusFilter, sourceFilter, sortBy, sortOrder]);

  useEffect(() => {
    const timer = setTimeout(() => { fetchContacts(); }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const fetchContacts = async () => {
    try {
      setLoading(true);
      const response = await crmAPI.getContacts({
        status: statusFilter, source: sourceFilter,
        search: searchTerm, sort_by: sortBy, sort_order: sortOrder,
      });
      setContacts(response.data);
      setSelectedContacts([]);
      setSelectAll(false);
    } catch { toast.error('Failed to load contacts'); }
    finally { setLoading(false); }
  };

  const handleCreateContact = async () => {
    if (!formData.name.trim() || !formData.phone.trim()) { toast.error('Name and phone are required'); return; }
    try {
      await crmAPI.createContact(formData);
      toast.success('Contact created');
      setShowAddDialog(false);
      setFormData(EMPTY_FORM);
      fetchContacts();
    } catch { toast.error('Failed to create contact'); }
  };

  const handleUpdateContact = async () => {
    if (!editingContact) return;
    try {
      await crmAPI.updateContact(editingContact.id, formData);
      toast.success('Contact updated');
      setShowEditDialog(false);
      setEditingContact(null);
      fetchContacts();
    } catch { toast.error('Failed to update contact'); }
  };

  const handleDeleteContact = async (contactId) => {
    try {
      await crmAPI.deleteContact(contactId);
      toast.success('Contact deleted');
      setDeleteConfirm(null);
      fetchContacts();
    } catch { toast.error('Failed to delete contact'); }
  };

  const handleMarkContacted = async (contactId) => {
    try { await crmAPI.markContacted(contactId); toast.success('Marked as contacted'); fetchContacts(); }
    catch { toast.error('Failed to update contact'); }
  };

  const handleBulkDelete = async () => {
    if (selectedContacts.length === 0) return;
    try {
      await crmAPI.bulkDelete(selectedContacts);
      toast.success(`${selectedContacts.length} contacts deleted`);
      setSelectedContacts([]); setSelectAll(false); fetchContacts();
    } catch { toast.error('Failed to delete contacts'); }
  };

  const handleBulkMarkContacted = async () => {
    if (selectedContacts.length === 0) return;
    try {
      await crmAPI.bulkMarkContacted(selectedContacts);
      toast.success(`${selectedContacts.length} contacts marked as contacted`);
      fetchContacts();
    } catch { toast.error('Failed to update contacts'); }
  };

  const handleImportCSV = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    try {
      const response = await crmAPI.importCSV(file);
      const r = response.data;
      toast.success(`${r.imported} contacts imported${r.errors?.length ? `, ${r.errors.length} errors` : ''}`);
      if (r.errors?.length) { r.errors.forEach(err => toast.error(err)); }
      fetchContacts();
    } catch { toast.error('Failed to import contacts'); }
    finally { setImporting(false); e.target.value = ''; }
  };

  const handleSendBulkMessage = async () => {
    if (selectedContacts.length === 0 || !messageText.trim()) { toast.error('Select contacts and enter a message'); return; }
    setSendingMessage(true);
    try {
      const payload = { contact_ids: selectedContacts, message: messageText, channel: messageChannel };
      if (messageChannel === 'email') payload.subject = messageSubject || 'Message from The Local Jewel';
      if (messageChannel === 'whatsapp' && whatsappMode === 'template' && templateName) {
        payload.template_name = templateName;
        payload.template_language = templateLanguage;
      }
      const result = await crmAPI.sendBulkMessage(payload);
      if (result.data.success > 0) toast.success(`Message sent to ${result.data.success} contacts`);
      if (result.data.failed > 0) toast.error(`Failed to send to ${result.data.failed} contacts`);
      if (result.data.errors?.length) result.data.errors.slice(0, 3).forEach(e => toast.error(e));
      setShowMessageDialog(false); setMessageText(''); setMessageSubject(''); setTemplateName(''); fetchContacts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send messages');
    } finally { setSendingMessage(false); }
  };

  const fetchWhatsAppTemplates = async () => {
    setLoadingTemplates(true);
    try {
      const r = await crmAPI.getWhatsAppTemplates();
      setAvailableTemplates(r.data.templates || []);
      if (r.data.error) toast.error(`Templates: ${r.data.error}`);
    } catch { setAvailableTemplates([]); }
    finally { setLoadingTemplates(false); }
  };

  const handleOpenWhatsApp = (phone) => {
    let cleanPhone = phone.replace(/\D/g, '');
    if (cleanPhone.length === 10) cleanPhone = '91' + cleanPhone;
    window.open(`https://wa.me/${cleanPhone}`, '_blank');
  };

  const handleEditClick = (contact) => {
    setEditingContact(contact);
    setFormData({
      name: contact.name, phone: contact.phone, email: contact.email || '',
      source: contact.source, status: contact.status, notes: contact.notes || '',
    });
    setShowEditDialog(true);
  };

  const handleSelectAll = (checked) => {
    setSelectAll(checked);
    setSelectedContacts(checked ? contacts.map(c => c.id) : []);
  };

  const handleSelectContact = (contactId, checked) => {
    if (checked) setSelectedContacts([...selectedContacts, contactId]);
    else { setSelectedContacts(selectedContacts.filter(id => id !== contactId)); setSelectAll(false); }
  };

  const fetchAPIKey = async () => {
    try { const r = await crmAPI.getAPIKey(); setApiKey(r.data.api_key); }
    catch { toast.error('Failed to get API key'); }
  };

  const handleRegenerateKey = async () => {
    setRegeneratingKey(true);
    try { const r = await crmAPI.regenerateAPIKey(); setApiKey(r.data.api_key); toast.success('API key regenerated'); }
    catch { toast.error('Failed to regenerate key'); }
    finally { setRegeneratingKey(false); }
  };

  const fetchMessagingConfig = async () => {
    try { const r = await crmAPI.getMessagingStatus(); setMessagingConfig(r.data); }
    catch { setMessagingConfig(null); }
  };

  const ContactFormFields = ({ data, onChange }) => (
    <div className="grid grid-cols-2 gap-4">
      <div className="col-span-2">
        <label className="block text-sm font-medium text-zinc-700 mb-1.5">Name <span className="text-red-500">*</span></label>
        <input type="text" value={data.name} onChange={e => onChange({ ...data, name: e.target.value })}
          placeholder="John Doe" data-testid="contact-name-input"
          className="w-full px-3 py-2 border border-zinc-200 rounded-md focus:outline-none focus:ring-2 focus:ring-zinc-200 text-sm" />
      </div>
      <div>
        <label className="block text-sm font-medium text-zinc-700 mb-1.5">Phone <span className="text-red-500">*</span></label>
        <input type="tel" value={data.phone} onChange={e => onChange({ ...data, phone: e.target.value })}
          placeholder="+91 98765 43210" data-testid="contact-phone-input"
          className="w-full px-3 py-2 border border-zinc-200 rounded-md focus:outline-none focus:ring-2 focus:ring-zinc-200 text-sm" />
      </div>
      <div>
        <label className="block text-sm font-medium text-zinc-700 mb-1.5">Email</label>
        <input type="email" value={data.email} onChange={e => onChange({ ...data, email: e.target.value })}
          placeholder="john@example.com" data-testid="contact-email-input"
          className="w-full px-3 py-2 border border-zinc-200 rounded-md focus:outline-none focus:ring-2 focus:ring-zinc-200 text-sm" />
      </div>
      <div>
        <label className="block text-sm font-medium text-zinc-700 mb-1.5">Source</label>
        <Select value={data.source} onValueChange={v => onChange({ ...data, source: v })}>
          <SelectTrigger data-testid="contact-source-select"><SelectValue /></SelectTrigger>
          <SelectContent>{SOURCES.filter(s => s.value !== 'all').map(s => (
            <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
          ))}</SelectContent>
        </Select>
      </div>
      <div>
        <label className="block text-sm font-medium text-zinc-700 mb-1.5">Status</label>
        <Select value={data.status} onValueChange={v => onChange({ ...data, status: v })}>
          <SelectTrigger data-testid="contact-status-select"><SelectValue /></SelectTrigger>
          <SelectContent>{STATUSES.filter(s => s.value !== 'all').map(s => (
            <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
          ))}</SelectContent>
        </Select>
      </div>
      <div className="col-span-2">
        <label className="block text-sm font-medium text-zinc-700 mb-1.5">Notes</label>
        <Textarea value={data.notes} onChange={e => onChange({ ...data, notes: e.target.value })}
          placeholder="Add any notes..." rows={2} data-testid="contact-notes-input" />
      </div>
    </div>
  );

  if (loading && contacts.length === 0) {
    return (
      <div className="animate-pulse space-y-6" data-testid="contacts-loading">
        <div className="h-8 w-48 bg-zinc-100 rounded" />
        <div className="space-y-4">{[1,2,3,4,5].map(i => <div key={i} className="h-16 bg-zinc-100 rounded" />)}</div>
      </div>
    );
  }

  return (
    <div className="space-y-6 fade-in" data-testid="contacts-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <Users className="w-8 h-8 text-zinc-700" />
          <div>
            <h1 className="text-3xl text-zinc-900" style={{ fontFamily: 'Playfair Display, serif' }}>Contacts</h1>
            <p className="text-sm text-zinc-500 mt-1">Manage your customers and leads</p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {isFounder() && (
            <Button variant="outline" size="sm" data-testid="api-key-btn"
              onClick={() => { fetchAPIKey(); setShowAPIKeyDialog(true); }}>
              <Key className="w-4 h-4 mr-2" />API Key
            </Button>
          )}
          <Button variant="outline" size="sm" data-testid="messaging-status-btn"
            onClick={() => { fetchMessagingConfig(); setShowMessagingStatus(true); }}>
            <Settings className="w-4 h-4 mr-2" />Integrations
          </Button>
          <input ref={fileInputRef} type="file" accept=".csv" onChange={handleImportCSV} className="hidden" />
          <Button variant="outline" size="sm" onClick={() => fileInputRef.current?.click()} disabled={importing}
            data-testid="import-csv-btn">
            {importing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
            Import CSV
          </Button>
          <Button onClick={() => { setFormData(EMPTY_FORM); setShowAddDialog(true); }}
            className="bg-zinc-900 hover:bg-zinc-800 text-white" data-testid="add-contact-btn">
            <Plus className="w-4 h-4 mr-2" />Add Contact
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col md:flex-row items-start md:items-center gap-4 bg-white p-4 rounded-lg border border-zinc-200">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
          <input type="text" placeholder="Search by name, phone or email..." value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)} data-testid="contact-search-input"
            className="w-full pl-10 pr-4 py-2 border border-zinc-200 rounded-md focus:outline-none focus:ring-2 focus:ring-zinc-200 text-sm" />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-40" data-testid="status-filter"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>{STATUSES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={sourceFilter} onValueChange={setSourceFilter}>
          <SelectTrigger className="w-40" data-testid="source-filter"><SelectValue placeholder="Source" /></SelectTrigger>
          <SelectContent>{SOURCES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={`${sortBy}-${sortOrder}`} onValueChange={v => { const [f, o] = v.split('-'); setSortBy(f); setSortOrder(o); }}>
          <SelectTrigger className="w-48" data-testid="sort-select"><SelectValue placeholder="Sort by" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="name-asc">Name (A-Z)</SelectItem>
            <SelectItem value="name-desc">Name (Z-A)</SelectItem>
            <SelectItem value="last_contacted-desc">Last Contacted (Recent)</SelectItem>
            <SelectItem value="last_contacted-asc">Last Contacted (Oldest)</SelectItem>
            <SelectItem value="created_at-desc">Created (Recent)</SelectItem>
            <SelectItem value="created_at-asc">Created (Oldest)</SelectItem>
          </SelectContent>
        </Select>
        <span className="text-sm text-zinc-500 whitespace-nowrap">{contacts.length} contact{contacts.length !== 1 ? 's' : ''}</span>
      </div>

      {/* Bulk Actions */}
      {selectedContacts.length > 0 && (
        <div className="flex items-center gap-4 bg-zinc-900 text-white p-4 rounded-lg" data-testid="bulk-actions-bar">
          <span className="text-sm font-medium">{selectedContacts.length} selected</span>
          <div className="flex-1" />
          <Button variant="ghost" size="sm" className="text-white hover:bg-zinc-800" onClick={handleBulkMarkContacted}
            data-testid="bulk-mark-contacted-btn">
            <CheckCircle className="w-4 h-4 mr-2" />Mark Contacted
          </Button>
          <Button variant="ghost" size="sm" className="text-white hover:bg-zinc-800"
            onClick={() => navigate(`/send-queue?contacts=${selectedContacts.join(',')}`)}
            data-testid="add-to-send-queue-btn">
            <Send className="w-4 h-4 mr-2" />Send Queue
          </Button>
          <Button variant="ghost" size="sm" className="text-white hover:bg-zinc-800"
            onClick={() => setShowMessageDialog(true)} data-testid="bulk-send-message-btn">
            <Send className="w-4 h-4 mr-2" />Send Message
          </Button>
          <Button variant="ghost" size="sm" className="text-red-400 hover:bg-zinc-800 hover:text-red-300"
            onClick={handleBulkDelete} data-testid="bulk-delete-btn">
            <Trash2 className="w-4 h-4 mr-2" />Delete
          </Button>
          <Button variant="ghost" size="sm" className="text-white hover:bg-zinc-800"
            onClick={() => { setSelectedContacts([]); setSelectAll(false); }}>
            <X className="w-4 h-4" />
          </Button>
        </div>
      )}

      {/* Contacts Table */}
      {contacts.length === 0 ? (
        <div className="text-center py-16 bg-zinc-50/50 rounded-lg" data-testid="contacts-empty">
          <Users className="w-16 h-16 text-zinc-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-zinc-700 mb-2">No contacts yet</h3>
          <p className="text-sm text-zinc-500 mb-6">Add your first contact or import from CSV</p>
          <div className="flex items-center justify-center gap-3">
            <Button variant="outline" onClick={() => fileInputRef.current?.click()}>
              <Upload className="w-4 h-4 mr-2" />Import CSV
            </Button>
            <Button onClick={() => { setFormData(EMPTY_FORM); setShowAddDialog(true); }}
              className="bg-zinc-900 hover:bg-zinc-800 text-white">
              <Plus className="w-4 h-4 mr-2" />Add Contact
            </Button>
          </div>
        </div>
      ) : (
        <div className="bg-white border border-zinc-200 rounded-lg overflow-hidden">
          <div className="grid grid-cols-12 gap-4 px-6 py-3 bg-zinc-50 border-b border-zinc-200 text-xs font-semibold text-zinc-500 uppercase tracking-widest">
            <div className="col-span-1 flex items-center">
              <Checkbox checked={selectAll} onCheckedChange={handleSelectAll} data-testid="select-all-checkbox" />
            </div>
            <div className="col-span-2">Name</div>
            <div className="col-span-2">Phone</div>
            <div className="col-span-2">Email</div>
            <div className="col-span-1">Source</div>
            <div className="col-span-1">Status</div>
            <div className="col-span-1">Contacted</div>
            <div className="col-span-2 text-right">Actions</div>
          </div>
          <div className="divide-y divide-zinc-100">
            {contacts.map(contact => (
              <div key={contact.id} data-testid={`contact-row-${contact.id}`}
                className={`grid grid-cols-12 gap-4 px-6 py-3.5 hover:bg-zinc-50 transition-colors items-center ${
                  selectedContacts.includes(contact.id) ? 'bg-zinc-50' : ''}`}>
                <div className="col-span-1">
                  <Checkbox checked={selectedContacts.includes(contact.id)}
                    onCheckedChange={c => handleSelectContact(contact.id, c)} />
                </div>
                <div className="col-span-2">
                  <p className="font-medium text-zinc-900 text-sm">{contact.name}</p>
                  {contact.notes && <p className="text-xs text-zinc-500 mt-0.5 truncate max-w-[180px]">{contact.notes}</p>}
                </div>
                <div className="col-span-2 text-sm text-zinc-700">{contact.phone}</div>
                <div className="col-span-2 text-sm text-zinc-500 truncate">{contact.email || '-'}</div>
                <div className="col-span-1">
                  <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${SOURCE_COLORS[contact.source] || SOURCE_COLORS.other}`}>
                    {contact.source}
                  </span>
                </div>
                <div className="col-span-1">
                  <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[contact.status] || STATUS_COLORS.lead}`}>
                    {contact.status}
                  </span>
                </div>
                <div className="col-span-1">
                  {contact.last_contacted
                    ? <span className="text-xs text-zinc-500">{formatRelativeTime(contact.last_contacted)}</span>
                    : <span className="text-xs text-zinc-400">Never</span>}
                </div>
                <div className="col-span-2 flex items-center justify-end gap-1">
                  <Button variant="ghost" size="icon" className="h-8 w-8 text-green-600 hover:bg-green-50"
                    onClick={() => handleOpenWhatsApp(contact.phone)} title="WhatsApp">
                    <MessageSquare className="w-4 h-4" />
                  </Button>
                  <Button variant="ghost" size="icon" className="h-8 w-8 text-blue-600 hover:bg-blue-50"
                    onClick={() => window.open(`tel:${contact.phone}`)} title="Call">
                    <Phone className="w-4 h-4" />
                  </Button>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-8 w-8"><MoreVertical className="w-4 h-4" /></Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => handleEditClick(contact)}>
                        <Edit className="w-4 h-4 mr-2" /> Edit
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleMarkContacted(contact.id)}>
                        <CheckCircle className="w-4 h-4 mr-2" /> Mark Contacted
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={() => setDeleteConfirm(contact)} className="text-red-600">
                        <Trash2 className="w-4 h-4 mr-2" /> Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Add Contact Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="sm:max-w-md bg-white">
          <DialogHeader>
            <DialogTitle className="text-xl" style={{ fontFamily: 'Playfair Display, serif' }}>Add New Contact</DialogTitle>
          </DialogHeader>
          <div className="py-4"><ContactFormFields data={formData} onChange={setFormData} /></div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddDialog(false)}>Cancel</Button>
            <Button onClick={handleCreateContact} className="bg-zinc-900 hover:bg-zinc-800 text-white"
              data-testid="confirm-add-contact-btn">Add Contact</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Contact Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="sm:max-w-md bg-white">
          <DialogHeader>
            <DialogTitle className="text-xl" style={{ fontFamily: 'Playfair Display, serif' }}>Edit Contact</DialogTitle>
          </DialogHeader>
          <div className="py-4"><ContactFormFields data={formData} onChange={setFormData} /></div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>Cancel</Button>
            <Button onClick={handleUpdateContact} className="bg-zinc-900 hover:bg-zinc-800 text-white"
              data-testid="confirm-edit-contact-btn">Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Send Message Dialog */}
      <Dialog open={showMessageDialog} onOpenChange={setShowMessageDialog}>
        <DialogContent className="sm:max-w-lg bg-white">
          <DialogHeader>
            <DialogTitle className="text-xl" style={{ fontFamily: 'Playfair Display, serif' }}>Send Message</DialogTitle>
            <DialogDescription>Send to {selectedContacts.length} selected contact{selectedContacts.length !== 1 ? 's' : ''}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="block text-sm font-medium text-zinc-700 mb-1.5">Channel</label>
              <Select value={messageChannel} onValueChange={(v) => { setMessageChannel(v); if (v !== 'whatsapp') setWhatsappMode('text'); }}>
                <SelectTrigger data-testid="message-channel-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="linq">Linq (iMessage/SMS)</SelectItem>
                  <SelectItem value="whatsapp">WhatsApp</SelectItem>
                  <SelectItem value="sms">SMS (Twilio)</SelectItem>
                  <SelectItem value="email">Email (SendGrid)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {messageChannel === 'whatsapp' && (
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-zinc-700 mb-1.5">Message Type</label>
                  <Select value={whatsappMode} onValueChange={(v) => { setWhatsappMode(v); if (v === 'template') fetchWhatsAppTemplates(); }}>
                    <SelectTrigger data-testid="whatsapp-mode-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="text">Free Text (24h window)</SelectItem>
                      <SelectItem value="template">Template Message</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-zinc-500 mt-1">
                    {whatsappMode === 'text'
                      ? 'Free text only works if the contact messaged you in the last 24 hours.'
                      : 'Templates work for outbound messaging to any contact. Must be pre-approved by Meta.'}
                  </p>
                </div>
                {whatsappMode === 'template' && (
                  <div>
                    <label className="block text-sm font-medium text-zinc-700 mb-1.5">Template</label>
                    {loadingTemplates ? (
                      <div className="flex items-center gap-2 py-2 text-sm text-zinc-500">
                        <Loader2 className="w-4 h-4 animate-spin" /> Loading templates...
                      </div>
                    ) : availableTemplates.length > 0 ? (
                      <Select value={templateName} onValueChange={setTemplateName}>
                        <SelectTrigger data-testid="template-select"><SelectValue placeholder="Select a template" /></SelectTrigger>
                        <SelectContent>
                          {availableTemplates.map(t => (
                            <SelectItem key={t.name} value={t.name}>
                              {t.name} ({t.language}) — {t.category}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <div className="space-y-2">
                        <input type="text" value={templateName} onChange={e => setTemplateName(e.target.value)}
                          placeholder="Enter template name (e.g. hello_world)"
                          data-testid="template-name-input"
                          className="w-full px-3 py-2 border border-zinc-200 rounded-md focus:outline-none focus:ring-2 focus:ring-zinc-200 text-sm" />
                        <p className="text-xs text-zinc-500">
                          Could not load templates. Enter the template name manually.
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
            {messageChannel === 'email' && (
              <div>
                <label className="block text-sm font-medium text-zinc-700 mb-1.5">Subject</label>
                <input type="text" value={messageSubject} onChange={e => setMessageSubject(e.target.value)}
                  placeholder="Email subject..." data-testid="message-subject-input"
                  className="w-full px-3 py-2 border border-zinc-200 rounded-md focus:outline-none focus:ring-2 focus:ring-zinc-200 text-sm" />
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-zinc-700 mb-1.5">
                {messageChannel === 'whatsapp' && whatsappMode === 'template' ? 'Body Parameter (optional)' : 'Message'}
              </label>
              <Textarea value={messageText} onChange={e => setMessageText(e.target.value)}
                placeholder={messageChannel === 'whatsapp' && whatsappMode === 'template'
                  ? 'Text to fill template body parameter...'
                  : 'Type your message...'}
                rows={4} data-testid="message-text-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowMessageDialog(false)}>Cancel</Button>
            <Button onClick={handleSendBulkMessage} disabled={sendingMessage}
              className="bg-zinc-900 hover:bg-zinc-800 text-white" data-testid="confirm-send-message-btn">
              {sendingMessage ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
              Send
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* API Key Dialog */}
      <Dialog open={showAPIKeyDialog} onOpenChange={setShowAPIKeyDialog}>
        <DialogContent className="sm:max-w-lg bg-white">
          <DialogHeader>
            <DialogTitle className="text-xl" style={{ fontFamily: 'Playfair Display, serif' }}>API Key for Lightning Bot</DialogTitle>
            <DialogDescription>Use this key to add contacts from your Telegram bot or external services</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="flex items-center gap-2 p-3 bg-zinc-100 rounded-lg">
              <code className="text-sm break-all flex-1 font-mono" data-testid="api-key-display">{apiKey || 'Loading...'}</code>
              <Button variant="ghost" size="icon" className="shrink-0" data-testid="copy-api-key-btn"
                onClick={() => { navigator.clipboard.writeText(apiKey); toast.success('Copied!'); }}>
                <Copy className="w-4 h-4" />
              </Button>
            </div>
            <div className="space-y-3 text-sm text-zinc-600">
              <p className="font-medium text-zinc-800">API Endpoint:</p>
              <code className="block p-2.5 bg-zinc-50 rounded text-xs font-mono">
                POST {process.env.REACT_APP_BACKEND_URL}/api/crm/contacts/external
              </code>
              <p className="font-medium text-zinc-800">Headers:</p>
              <code className="block p-2.5 bg-zinc-50 rounded text-xs font-mono whitespace-pre">
{`Content-Type: application/json
X-API-Key: <your-key>`}
              </code>
              <p className="font-medium text-zinc-800">Body:</p>
              <code className="block p-2.5 bg-zinc-50 rounded text-xs font-mono whitespace-pre">
{`{
  "name": "John Doe",
  "phone": "+919876543210",
  "email": "john@example.com",
  "source": "whatsapp",
  "status": "lead",
  "notes": "From Lightning bot"
}`}
              </code>
            </div>
          </div>
          <DialogFooter className="flex gap-2">
            <Button variant="outline" size="sm" onClick={handleRegenerateKey} disabled={regeneratingKey}
              data-testid="regenerate-api-key-btn">
              {regeneratingKey ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
              Regenerate
            </Button>
            <Button onClick={() => setShowAPIKeyDialog(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Messaging Integrations Status Dialog */}
      <Dialog open={showMessagingStatus} onOpenChange={setShowMessagingStatus}>
        <DialogContent className="sm:max-w-md bg-white">
          <DialogHeader>
            <DialogTitle className="text-xl" style={{ fontFamily: 'Playfair Display, serif' }}>Messaging Integrations</DialogTitle>
            <DialogDescription>Status of configured messaging channels</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-4">
            {messagingConfig ? Object.entries(messagingConfig).map(([channel, info]) => (
              <div key={channel} className="flex items-center justify-between p-3.5 bg-zinc-50 rounded-lg border border-zinc-100"
                data-testid={`messaging-status-${channel}`}>
                <div className="flex items-center gap-3">
                  {channel === 'linq' && <MessageSquare className="w-5 h-5 text-blue-600" />}
                  {channel === 'whatsapp' && <MessageSquare className="w-5 h-5 text-green-600" />}
                  {channel === 'sms' && <Phone className="w-5 h-5 text-blue-600" />}
                  {channel === 'email' && <Mail className="w-5 h-5 text-purple-600" />}
                  <div>
                    <p className="text-sm font-medium text-zinc-900 capitalize">{channel}</p>
                    <p className="text-xs text-zinc-500">{info.provider}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {info.configured ? (
                    <span className="flex items-center gap-1.5 text-xs font-medium text-emerald-700 bg-emerald-50 px-2.5 py-1 rounded-full">
                      <Wifi className="w-3 h-3" /> Connected
                    </span>
                  ) : (
                    <span className="flex items-center gap-1.5 text-xs font-medium text-zinc-500 bg-zinc-100 px-2.5 py-1 rounded-full">
                      <WifiOff className="w-3 h-3" /> Not configured
                    </span>
                  )}
                </div>
              </div>
            )) : (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-5 h-5 animate-spin text-zinc-400" />
              </div>
            )}
            <p className="text-xs text-zinc-500 mt-2">
              To configure integrations, add the required API keys to the backend environment variables.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={fetchMessagingConfig}>
              <RefreshCw className="w-4 h-4 mr-2" />Refresh
            </Button>
            <Button onClick={() => setShowMessagingStatus(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <DialogContent className="sm:max-w-md bg-white">
          <DialogHeader>
            <DialogTitle>Delete Contact</DialogTitle>
            <DialogDescription>Are you sure you want to delete {deleteConfirm?.name}? This cannot be undone.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirm(null)}>Cancel</Button>
            <Button onClick={() => handleDeleteContact(deleteConfirm.id)}
              className="bg-red-600 hover:bg-red-700 text-white" data-testid="confirm-delete-btn">Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Contacts;
